import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  TextField,
  Typography,
  InputLabel,
  MenuItem,
  Select,
  Chip,
  Checkbox,
  Link,
} from '@material-ui/core';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { submitLeadRequest } from 'apis';
import { isMobile } from 'utils/screen';
import { useAlert, useLanguage } from 'hooks';
import { useIntl } from 'react-intl';
import ReCAPTCHA from 'react-google-recaptcha';
import { startsWith } from 'lodash';
import { Broker } from 'models';
import {
  COLOR,
  ALERT,
  BROKER_SERVICE_OFFERS_REQUEST_PAGE,
  CLIENT_SERVICE_OFFERS_REQUEST_PAGE,
  CAPTCHA_KEY,
} from 'config/constants';
import { HvButton } from 'components';
import { CancelRounded } from '@material-ui/icons';
import FormHeader from './FormHeader';

const useStyles = makeStyles({
  row: {
    marginBottom: 8,
    height: '100%',
  },
  twoColumnsRow: {
    justifyContent: 'space-between',
    flexDirection: 'row',
    display: 'flex',
    marginBottom: 8,
  },
  nameInputContent: {
    width: '49%',
    marginBottom: 8,
  },
  mobileNameInputContent: {
    width: '100%',
    marginBottom: 8,
  },
  inline: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  input: {
    borderRadius: 5,
    borderBottom: 'none',
    '&.MuiInput-underline:before': {
      borderBottom: 'none',
    },
    '&.MuiInput-underline:after': {
      borderBottom: 'none',
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: 'transparent',
    },
    '&.MuiFormHelperText-root.Mui-error': {
      backgroundColor: 'transparent',
    },
    '& .MuiOutlinedInput-input': {
      backgroundColor: 'white',
    },
  },
  messageInput: {
    backgroundColor: 'white',
    borderRadius: 5,
    borderBottom: 'none',
  },
  inputTtitle: {
    fontSize: 12,
    marginBottom: 5,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    color: COLOR.BLACK,
    maxWidth: 430,
  },
  chip: {
    margin: 2,
  },
  icon: {
    color: COLOR.WHITE,
  },
  selectedMenuItem: {
    color: COLOR.WHITE,
    backgroundColor: COLOR.DARK_BLUE,
    '&.MuiListItem-root.Mui-selected': {
      backgroundColor: COLOR.DARK_BLUE,
    },
  },
  menuItem: {
    '&.MuiListItem-root.Mui-selected': {
      color: COLOR.WHITE,
      backgroundColor: COLOR.DARK_BLUE,
    },
  },
  button: {
    width: '100%',
  },
  checkbox: {
    color: COLOR.DARK_BLUE,
    '&.MuiCheckbox-colorSecondary.Mui-checked': {
      color: COLOR.DARK_BLUE,
    },
  },
  checkboxError: {
    color: '#f00',
    '&.MuiCheckbox-colorSecondary.Mui-checked': {
      color: COLOR.DARK_BLUE,
    },
  },
  policyText: {
    fontSize: 12,
  },
  blueText: {
    color: COLOR.DARK_BLUE,
    textDecoration: 'underline',
  },
  error: {
    color: '#f00',
    fontSize: 12,
    marginLeft: 10,
    marginTop: 4,
  },
});
interface Props {
  className: string;
  isBroker: boolean;
  setIsSubmited: (value: boolean) => void;
  showHeader: boolean;
  broker?: Broker;
  portal_infos?: any;
  primary_bg_text_color?: string;
  secondary_bg_text_color?: string;
  showServiceOptions?: boolean;
}
interface ParamTypes {
  broker_id: string;
}
function RequestForm(props: Props) {
  const {
    className,
    isBroker,
    setIsSubmited,
    showHeader,
    broker,
    portal_infos,
    primary_bg_text_color,
    secondary_bg_text_color,
    showServiceOptions,
  } = props;
  const intl = useIntl();
  const styles = useStyles();
  const params = useParams<ParamTypes>();
  let broker_id;
  if (params && params.broker_id) {
    broker_id = Number(params.broker_id);
  }
  if (broker) {
    broker_id = broker.id;
  }
  const primary_color = portal_infos?.template_style?.primary_color || COLOR.LIGHT_BLUE;
  const secondary_color = portal_infos?.template_style?.secondary_color || COLOR.SECONDARY_BLUE;

  const recaptchaRef = useRef(null);
  const services = isBroker
    ? BROKER_SERVICE_OFFERS_REQUEST_PAGE
    : CLIENT_SERVICE_OFFERS_REQUEST_PAGE;
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    selectedServices: [],
    message: '',
  });
  const [errors, setErrors] = useState({
    firstname: false,
    lastname: false,
    email: false,
    phone: false,
    checkboxError: false,
    captchaError: false,
  });
  const [isAccepted, setIsAccepted] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const { displayAlert } = useAlert();
  useEffect(() => {}, [formData, errors]);
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'firstname' && value) {
      setErrors({ ...errors, firstname: false });
    }
    if (name === 'lastname' && value) {
      setErrors({ ...errors, lastname: false });
    }
    if (name === 'email' && value) {
      setErrors({ ...errors, email: false });
    }
  };
  const handlePhoneChange = (value) => {
    setFormData({ ...formData, phone: value });
  };
  const validatePhoneNumber = (inputNumber, country, countries) => {
    return countries.some(() => {
      return startsWith(inputNumber, country.dialCode) || startsWith(country.dialCode, inputNumber);
    });
  };
  const handleSelectChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setFormData({ ...formData, selectedServices: event.target.value as string[] });
  };
  const handleDelete = (e: React.MouseEvent, chipToDelete: string) => {
    e.preventDefault();
    const { selectedServices } = formData;
    const newSelectedServices = selectedServices.filter((selectedService) => {
      return selectedService !== chipToDelete;
    });
    setFormData({ ...formData, selectedServices: newSelectedServices as string[] });
  };
  const handleOnAccept = (e) => {
    setIsAccepted(!isAccepted);
    if (e.target.checked) {
      setErrors({ ...errors, checkboxError: false });
    }
  };
  const onCaptchaChange = (value) => {
    setIsVerified(true);
    setErrors({ ...errors, captchaError: false });
  };
  const onCaptchaExpired = () => {
    setIsVerified(false);
    setErrors({ ...errors, captchaError: true });
  };
  const onCaptchaErrored = () => {
    setIsVerified(false);
    setErrors({ ...errors, captchaError: true });
  };
  const handleSubmit = async (event) => {
    const { firstname, lastname, email } = formData;
    // event.preventDefault();
    const validationErrors = {
      firstname: false,
      lastname: false,
      email: false,
      phone: false,
      checkboxError: false,
      captchaError: false,
    };
    // do something with the form data, such as sending it to a server
    if (!firstname) {
      validationErrors.firstname = true;
    }
    if (!lastname) {
      validationErrors.lastname = true;
    }
    if (!email) {
      validationErrors.email = true;
    }
    if (!isAccepted) {
      validationErrors.checkboxError = true;
    }
    if (!isVerified) {
      validationErrors.captchaError = true;
    }
    setErrors(validationErrors);
    if (firstname && lastname && email && isAccepted && isVerified) {
      let data = formData;
      if (broker_id) {
        data = { ...formData, ...{ destinate_user_id: Number(broker_id) } };
      }
      const res = await submitLeadRequest(data);
      if (res) {
        displayAlert(ALERT.SUCCESS, intl.formatMessage({ id: 'serviceRequest.success' }));
        setIsSubmited(true);
        // reset the form
        setFormData({
          firstname: '',
          lastname: '',
          email: '',
          phone: '',
          selectedServices: [],
          message: '',
        });
      } else {
        displayAlert(ALERT.ERROR, intl.formatMessage({ id: 'serviceRequest.error' }));
      }
    }
  };

  return (
    <Box className={className}>
      {showHeader ? <FormHeader /> : null}
      <form>
        <Box className={isMobile() ? null : styles.twoColumnsRow}>
          <Box className={isMobile() ? styles.mobileNameInputContent : styles.nameInputContent}>
            <TextField
              id='firstname'
              label={intl.formatMessage({ id: 'serviceRequest.firstname' })}
              className={styles.input}
              name='firstname'
              variant='outlined'
              size='small'
              required
              error={errors.firstname}
              fullWidth
              value={formData.firstname}
              onChange={handleInputChange}
              helperText={
                errors.firstname ? intl.formatMessage({ id: 'serviceRequest.required' }) : null
              }
            />
          </Box>
          <Box className={isMobile() ? styles.mobileNameInputContent : styles.nameInputContent}>
            <TextField
              id='lastname'
              label={intl.formatMessage({ id: 'serviceRequest.lastname' })}
              className={styles.input}
              name='lastname'
              variant='outlined'
              size='small'
              required
              fullWidth
              value={formData.lastname}
              onChange={handleInputChange}
              error={errors.lastname}
              helperText={
                errors.lastname ? intl.formatMessage({ id: 'serviceRequest.required' }) : null
              }
            />
          </Box>
        </Box>
        <Box className={styles.row}>
          <TextField
            id='email'
            label={intl.formatMessage({ id: 'serviceRequest.email' })}
            className={styles.input}
            name='email'
            variant='outlined'
            size='small'
            required
            fullWidth
            value={formData.email}
            onChange={handleInputChange}
            error={errors.email}
            helperText={errors.email ? intl.formatMessage({ id: 'serviceRequest.required' }) : null}
          />
        </Box>
        <Box className={styles.row}>
          <PhoneInput
            country='ca'
            preferredCountries={['ca', 'us']}
            enableSearch
            value={`+${formData.phone}`}
            placeholder={intl.formatMessage({ id: 'serviceRequest.phone' })}
            inputProps={{
              name: intl.formatMessage({ id: 'serviceRequest.phone' }),
              required: false,
            }}
            prefix='+'
            countryCodeEditable={false}
            onChange={handlePhoneChange}
            isValid={(inputNumber, country: any, countries) => {
              return validatePhoneNumber(inputNumber, country, countries);
            }}
            containerStyle={{ marginBottom: 5, marginTop: 5 }}
            inputStyle={{ margin: 0, width: '100%', height: 40 }}
            dropdownStyle={{ color: COLOR.BLACK }}
            defaultErrorMessage={
              errors.phone ? intl.formatMessage({ id: 'serviceRequest.required' }) : null
            }
          />
        </Box>
        {showServiceOptions ? (
          <Box className={styles.row}>
            <InputLabel className={styles.inputTtitle} style={{ color: secondary_color }}>
              {intl.formatMessage({ id: 'serviceRequest.services' })}
            </InputLabel>
            <Select
              id='services'
              className={styles.input}
              style={{ backgroundColor: COLOR.WHITE }}
              multiple
              fullWidth
              MenuProps={{
                getContentAnchorEl: null,
              }}
              value={formData.selectedServices}
              onChange={handleSelectChange}
              renderValue={(selected) => {
                return (
                  <div className={styles.chips}>
                    {(selected as string[]).map((value) => {
                      return (
                        <Chip
                          key={value}
                          label={value}
                          clickable
                          deleteIcon={
                            <CancelRounded
                              onMouseDown={(event) => {
                                return event.stopPropagation();
                              }}
                              className={styles.icon}
                            />
                          }
                          color='primary'
                          className={styles.chip}
                          onDelete={(e) => {
                            return handleDelete(e, value);
                          }}
                        />
                      );
                    })}
                  </div>
                );
              }}
            >
              {services.map((service) => {
                return (
                  <MenuItem
                    key={service.key}
                    value={intl.formatMessage({ id: `services.${[service.key]}` })}
                    className={
                      formData.selectedServices.includes(service)
                        ? styles.selectedMenuItem
                        : styles.menuItem
                    }
                  >
                    {intl.formatMessage({ id: `services.${[service.key]}` })}
                  </MenuItem>
                );
              })}
            </Select>
          </Box>
        ) : null}
        <Box className={styles.row}>
          <TextField
            id='message'
            label={intl.formatMessage({ id: 'serviceRequest.message' })}
            className={styles.messageInput}
            name='message'
            variant='outlined'
            multiline
            fullWidth
            // rows={2}
            minRows={2}
            value={formData.message}
            onChange={handleInputChange}
          />
        </Box>
        <Box className={styles.inline}>
          <Checkbox
            checked={isAccepted}
            onChange={handleOnAccept}
            className={errors.checkboxError ? styles.checkboxError : styles.checkbox}
            style={{ color: secondary_color }}
          />
          <Typography className={styles.policyText} style={{ color: secondary_color }}>
            {intl.formatMessage({ id: 'serviceRequest.accept' })}
            <Link
              href='https://hausvalet.ca/terms-of-use/'
              target='_blank'
              rel='noreferrer'
              style={{ color: secondary_color }}
            >
              <span className={styles.blueText} style={{ color: primary_bg_text_color }}>
                {intl.formatMessage({ id: 'serviceRequest.terms' })}
              </span>
            </Link>
            {intl.formatMessage({ id: 'serviceRequest.and' })}
            <Link
              href='https://hausvalet.ca/privacy-policy/'
              target='_blank'
              rel='noreferrer'
              style={{ color: secondary_color }}
            >
              <span className={styles.blueText} style={{ color: primary_bg_text_color }}>
                {intl.formatMessage({ id: 'serviceRequest.policy' })}
              </span>
            </Link>
            . *
          </Typography>
        </Box>
        <Box className={styles.row}>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={CAPTCHA_KEY}
            onChange={onCaptchaChange}
            onExpired={onCaptchaExpired}
            onErrored={onCaptchaErrored}
          />
          {errors.captchaError ? (
            <Typography className={styles.error}>
              {intl.formatMessage({ id: 'serviceRequest.required' })}
            </Typography>
          ) : null}
        </Box>
        <HvButton
          className={styles.button}
          style={{ backgroundColor: secondary_color, color: secondary_bg_text_color }}
          onClick={handleSubmit}
        >
          {intl.formatMessage({ id: 'serviceRequest.sendMessage' })}
        </HvButton>
      </form>
    </Box>
  );
}

export default RequestForm;
